import { useCallback, useMemo, useSyncExternalStore } from "react";
import { MultiplayerOrchestrator } from "./MultiplayerOrchestrator";
import {
  createInitialViewState,
  MultiplayerViewState,
} from "./MultiplayerViewState";

/**
 * React hook that subscribes to a MultiplayerOrchestrator instance
 * using useSyncExternalStore so that all consumers stay consistent
 * across concurrent rendering.
 */
export function useMultiplayerOrchestrator(
  orchestrator: MultiplayerOrchestrator | null
): MultiplayerViewState {
  const fallbackState = useMemo(() => createInitialViewState(), []);

  const subscribe = useCallback(
    (listener: (state: MultiplayerViewState) => void) => {
      if (!orchestrator) {
        listener(fallbackState);
        return () => {};
      }
      return orchestrator.subscribe(listener);
    }, [orchestrator, fallbackState]
  );

  const getSnapshot = useCallback(() => {
    return orchestrator?.getViewState() ?? fallbackState;
  }, [orchestrator, fallbackState]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

